import { useCallback, useEffect, useMemo, useState } from 'react';
import { Location } from 'react-router-dom';
import { useRouter } from '~/utils/navigation';
import { lastProduct as getLastProduct } from '~/store/cookies';
import useContextualPathTransfomer from '~/hooks/use-contextual-path-transformer';
import { getBasePathName } from '~/utils/pathname';
import useQueryCodec from './use-query-codec';
import { SIDEBAR } from '~/ui/ui-constants';
import { reportMessage } from '@comeon/error-reporter';
import useConfiguration from '~/configuration/use-configuration';

type Options = {
	preserveSidebarState?: boolean;
	preserveContexts?: boolean;
	location?: Location;
};

function useRouterCallbacks() {
	const router = useRouter();
	const [newLocation, setNewLocation] = useState('');
	const transformPath = useContextualPathTransfomer();
	const codec = useQueryCodec(SIDEBAR); // TODO fix this.its experimental, using query coder to detect safari location problems
	const { license } = useConfiguration();

	// memoize this, since it only needs to be calculated calculated once
	const basePath = useMemo(() => {
		const lastProd = getLastProduct() as string; // TODO: fix me when we move cookies to TS
		const theBasePathname = getBasePathName(license, lastProd);
		return lastProd ? theBasePathname() : '';
	}, [license]);

	const transformPathAndPush = useCallback(
		(path: string, state: Record<string, unknown> = {}, responsiveOptions?: Options) => {
			const location = transformPath(path, responsiveOptions);
			setNewLocation(location);
			return router.push(location, { state });
		},
		[router, transformPath]
	);

	const transformPathAndReplace = useCallback(
		(path: string, state: Record<string, string> = {}, responsiveOptions?: Options) => {
			// TODO (navigation) - use search params or redux for state
			return router.replace(transformPath(path, responsiveOptions), { state });
		},
		[router, transformPath]
	);

	// TODO fix this.its experimental, detect safari location problems for login callbacks and log it in sentry
	// this is a workaround for safari not updating the location search when using navigate
	useEffect(() => {
		if (newLocation === '') return;
		const { pathname: decodedWindowLocation } = codec?.queryDecoder(window.location) || {};
		const { pathname: decodedNewLocation } = codec?.queryDecoder(newLocation) || {};
		if (decodedWindowLocation !== decodedNewLocation) {
			// @ts-expect-error - $FixMe Sentry types needs to be updated to allow optional arguments, info need to be same enum as severityLevel
			reportMessage('Safari Location Update blocked', {
				severityLevel: 'info',
				extra: { decodedWindowLocation, decodedNewLocation }
			});
			// eslint-disable-next-line no-console
			console.log('new location search didnt update');
		}
	}, [newLocation, codec]);

	const pushBack = useCallback(() => {
		// TODO - history no longer available in v6, is there a better way
		if (window.history.length > 1) {
			return router.back();
		}
		return router.push(basePath);
	}, [router, basePath]);

	return {
		pushBack,
		transformPathAndPush,
		transformPathAndReplace,
		basePath
	};
}

export default useRouterCallbacks;
